/**
 * Prompt Settings
 * Configure the active model and parameters for a prompt.
 * Changes here affect production behavior.
 */

import { useState, useEffect, useMemo } from 'react';
import apiClient from '../../lib/api';
import { toast } from 'sonner';

interface ModelOption {
  id: string;
  name: string;
  provider: string;
  pricing?: { prompt: number; completion: number };
  contextLength?: number;
}

interface PromptSettingsProps {
  promptKey: string;
  currentMetadata: Record<string, unknown>;
  onSaved: () => void;
}

export default function PromptSettings({
  promptKey,
  currentMetadata,
  onSaved,
}: PromptSettingsProps) {
  const [models, setModels] = useState<ModelOption[]>([]);
  const [selectedModel, setSelectedModel] = useState(
    (currentMetadata?.model as string) || 'gpt-4o-mini'
  );
  const [temperature, setTemperature] = useState(
    (currentMetadata?.temperature as number) ?? 0.7
  );
  const [maxTokens, setMaxTokens] = useState(
    (currentMetadata?.maxTokens as number) || 1000
  );
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    apiClient.get('/admin/prompts/models').then((res) => {
      setModels(res.data.models || []);
    }).catch(() => {});
  }, []);

  const activeModel = (currentMetadata?.model as string) || 'gpt-4o-mini';
  const hasChanges =
    selectedModel !== activeModel ||
    temperature !== ((currentMetadata?.temperature as number) ?? 0.7) ||
    maxTokens !== ((currentMetadata?.maxTokens as number) || 1000);

  const filteredModels = useMemo(() => {
    if (!modelSearch) return models;
    const q = modelSearch.toLowerCase();
    return models.filter(
      (m) => m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)
    );
  }, [models, modelSearch]);

  const groupedModels = useMemo(() => {
    const groups: Record<string, ModelOption[]> = {};
    for (const m of filteredModels) {
      if (!groups[m.provider]) groups[m.provider] = [];
      groups[m.provider].push(m);
    }
    return groups;
  }, [filteredModels]);

  const selectedModelObj = models.find((m) => m.id === selectedModel);
  const activeModelObj = models.find((m) => m.id === activeModel);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.post(
        `/admin/prompts/${encodeURIComponent(promptKey)}/settings`,
        { model: selectedModel, temperature, maxTokens }
      );
      toast.success('Instellingen opgeslagen');
      setShowConfirm(false);
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setIsSaving(false);
    }
  };

  function formatPrice(price: number): string {
    if (price === 0) return 'gratis';
    if (price < 0.01) return '<$0.01';
    return `$${price.toFixed(2)}`;
  }

  return (
    <div className="space-y-6">
      {/* Warning banner */}
      <div className="p-4 border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/20">
        <div className="flex items-start gap-3">
          <span className="text-lg">!</span>
          <div>
            <p className="text-sm font-bold text-orange-800 dark:text-orange-300">
              Productie-instellingen
            </p>
            <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
              Wijzigingen hier worden direct doorgevoerd in de app. Gebruik de Test tab om eerst te experimenteren met andere modellen.
            </p>
          </div>
        </div>
      </div>

      {/* Current active config */}
      <div className="p-4 border-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">Huidige configuratie (actief)</h4>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-500">Model</span>
            <div className="font-mono font-bold text-black dark:text-white">{activeModelObj?.name || activeModel}</div>
            {activeModelObj?.pricing && (
              <div className="text-xs text-gray-400">
                {formatPrice(activeModelObj.pricing.prompt)} in / {formatPrice(activeModelObj.pricing.completion)} out per 1M
              </div>
            )}
          </div>
          <div>
            <span className="text-xs text-gray-500">Temperature</span>
            <div className="font-mono font-bold text-black dark:text-white">{(currentMetadata?.temperature as number) ?? 0.7}</div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Max tokens</span>
            <div className="font-mono font-bold text-black dark:text-white">{(currentMetadata?.maxTokens as number) || 1000}</div>
          </div>
        </div>
      </div>

      {/* Edit settings */}
      <div className="space-y-4">
        <h4 className="text-sm font-bold text-black dark:text-white">Instellingen wijzigen</h4>

        {/* Model selector */}
        <div className="relative">
          <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">Model</label>
          <button
            onClick={() => setShowModelDropdown(!showModelDropdown)}
            className="w-full px-3 py-2 text-sm text-left border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none flex items-center justify-between"
          >
            <span className="truncate">{selectedModelObj?.name || selectedModel}</span>
            <span className="flex items-center gap-3 ml-2 shrink-0">
              {selectedModelObj?.pricing && (
                <span className="text-xs text-gray-500">
                  {formatPrice(selectedModelObj.pricing.prompt)} / {formatPrice(selectedModelObj.pricing.completion)} /1M
                </span>
              )}
              <span className="text-xs text-gray-400">{selectedModel.includes('/') ? 'OpenRouter' : 'OpenAI'}</span>
            </span>
          </button>

          {showModelDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => { setShowModelDropdown(false); setModelSearch(''); }} />
              <div className="absolute z-20 top-full left-0 right-0 mt-1 border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-h-64 flex flex-col">
                <input
                  type="text"
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  placeholder="Zoek model..."
                  className="w-full px-3 py-2 text-sm border-b-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none"
                  autoFocus
                />
                <div className="overflow-y-auto flex-1">
                  {Object.entries(groupedModels).map(([provider, providerModels]) => (
                    <div key={provider}>
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 sticky top-0">
                        {provider} ({providerModels.length})
                      </div>
                      {providerModels.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setSelectedModel(m.id);
                            setShowModelDropdown(false);
                            setModelSearch('');
                          }}
                          className={`w-full px-3 py-1.5 text-sm text-left font-mono hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                            selectedModel === m.id ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black dark:text-white'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{m.name}</span>
                            {m.pricing && (
                              <span className={`text-xs shrink-0 ${selectedModel === m.id ? 'opacity-70' : 'text-gray-400'}`}>
                                {formatPrice(m.pricing.prompt)} / {formatPrice(m.pricing.completion)}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Temperature */}
        <div>
          <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">
            Temperature ({temperature})
          </label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={temperature}
            onChange={(e) => setTemperature(parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Precies (0)</span>
            <span>Creatief (2)</span>
          </div>
        </div>

        {/* Max tokens */}
        <div>
          <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">Max tokens output</label>
          <input
            type="number"
            value={maxTokens}
            onChange={(e) => setMaxTokens(parseInt(e.target.value) || 1000)}
            min={100}
            max={16000}
            step={100}
            className="w-32 px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono focus:outline-none"
          />
        </div>

        {/* Save button with confirmation */}
        {hasChanges && !showConfirm && (
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 text-sm font-bold border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white transition-colors"
          >
            Wijzigingen toepassen...
          </button>
        )}

        {showConfirm && (
          <div className="p-4 border-2 border-red-500 bg-red-50 dark:bg-red-950/20 space-y-3">
            <p className="text-sm font-bold text-red-800 dark:text-red-300">
              Weet je het zeker?
            </p>
            <p className="text-xs text-red-700 dark:text-red-400">
              Dit wijzigt het model voor <strong>{promptKey}</strong> in productie.
              {selectedModel !== activeModel && (
                <span> Model: <strong>{activeModelObj?.name || activeModel}</strong> wordt <strong>{selectedModelObj?.name || selectedModel}</strong>.</span>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-bold border-2 border-red-600 bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? 'Opslaan...' : 'Ja, doorvoeren'}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm border-2 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
              >
                Annuleer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
